import * as cleanAxios from 'axios';
import { isCancel } from 'axios';
import { useContext, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import useAxios from './useAxios';
import { OnlyFansInit } from './useSubscriberInit';

export namespace OnlyFansFileUploadResponse {
    export interface CreateSignedUpload {
        uploadId?: string;
        keys?: { part: number; putUrl: string }[];
        putUrl?: string;
        getUrl: string;
        uploadToken?: string;
    }

    export interface Additional {
        user: string;
    }

    export interface Thumb {
        id: number;
        url: string;
    }

    export interface Response {
        processId: string;
        host: string;
        sourceUrl: string;
        extra: string;
        additional: Additional;
        thumbs: Thumb[];
    }

    export interface FinishResponse {
        ETag: string;
    }
}

const useSubscriberUpload = () => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const params = useParams();
    const axios = useAxios();

    const [uploading, setUploading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [progress, setProgress] = useState<number>(0);
    const [eta, setEta] = useState<number | null>(null);
    const [uploadSpeed, setUploadSpeed] = useState<number>(0);
    const [currentFileName, setCurrentFileName] = useState<string>('');
    const [totalFiles, setTotalFiles] = useState<number>(0);
    const [currentFileIndex, setCurrentFileIndex] = useState<number>(0);
    const abortControllerRef = useRef<AbortController | null>(null);
    const uploadStartTimeRef = useRef<number | null>(null);
    const lastProgressRef = useRef({ loaded: 0, time: 0 });
    const totalBytesRef = useRef<number>(0);
    const uploadedBytesRef = useRef<number>(0);
    const partProgressRef = useRef<Map<string, number>>(new Map()); // Track progress per part
    const progressUpdateIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const smoothedSpeedRef = useRef<number>(0);

    /**
     * Creates a sign key for the given file
     */
    const createSignKeyForFile = (
        onlyFansInit: OnlyFansInit.Init,
        filePath: string,
        mimetype: string,
    ): {
        fileName: string;
        signKey: string;
        mimetype: string;
    } => {
        const fileName = filePath.split('/').pop() || filePath;
        const randomTimeHash = (Math.random() * new Date().getTime()).toFixed(0);
        const signKey = [onlyFansInit.upload.s3.uploadPath, uuidv4(), randomTimeHash, fileName].join('/').replace('//', '/');

        return {
            fileName,
            signKey,
            mimetype,
        };
    };

    /**
     * Creates a signed upload URL for the given file
     */
    const createSignedUpload = async (
        filename: string,
        signKey: string,
        mimetype: string,
        parts: number,
    ): Promise<OnlyFansFileUploadResponse.CreateSignedUpload | null> => {
        if (userContext.jwtToken && settingsContext.apiKey && userContext.user.username) {
            const query: string = `${settingsContext.routes.onlyFans.base}${params.userId}/vaultMediaCreateSignedUpload`;

            return axios
                .post(query, {
                    filename,
                    signKey,
                    mimetype,
                    parts,
                })
                .then(async response => {
                    if (response.status === 200) {
                        return response.data;
                    }
                    return null;
                })
                .catch(error => {
                    console.error(error);
                    return null;
                });
        }
        return null;
    };

    /**
     * Checks whether a file already exists in the Vault by its S3-ETag style hash.
     * Returns true if a matching media already exists, false otherwise (including on error,
     * so a failed check never blocks an upload).
     */
    const checkVaultMediaHashExists = async (hash: string, size: number): Promise<boolean> => {
        if (userContext.jwtToken && settingsContext.apiKey && userContext.user.username) {
            const query: string = `${settingsContext.routes.onlyFans.base}${params.userId}/vaultMediaCheckHash`;

            return axios
                .post(query, { hash, size })
                .then(response => (response.status === 200 ? Boolean(response.data?.exists) : false))
                .catch(error => {
                    console.error(error);
                    return false;
                });
        }
        return false;
    };

    const finishSignedMultipartUpload = async (
        uploadId: string,
        signKey: string,
        parts: { PartNumber: number; ETag: string }[],
        secure: boolean,
    ): Promise<OnlyFansFileUploadResponse.FinishResponse | null> => {
        if (userContext.jwtToken && settingsContext.apiKey && userContext.user.username) {
            const query: string = `${settingsContext.routes.onlyFans.base}${params.userId}/vaultMediaFinishUpload`;

            return axios
                .post(query, {
                    uploadId,
                    signKey,
                    parts,
                    secure,
                })
                .then(async response => {
                    if (response.status === 200) {
                        return response.data;
                    }
                    return null;
                })
                .catch(error => {
                    console.error(error);
                    return null;
                });
        }
        return null;
    };

    const sendUploadDataToConverter = async (fileData: {
        fileName: string;
        signKey: string;
        mimetype: string;
        location: string;
        eTag: string;
        uploadToken?: string | null;
    }): Promise<OnlyFansFileUploadResponse.Response | null> => {
        if (userContext.jwtToken && settingsContext.apiKey && userContext.user.username) {
            const query: string = `${settingsContext.routes.onlyFans.base}${params.userId}/vaultMediaSendUploadDataToConverter`;

            return axios
                .post(query, fileData)
                .then(async response => {
                    if (response.status === 200) {
                        return response.data;
                    }
                    return null;
                })
                .catch(error => {
                    console.error(error);
                    return null;
                });
        }
        return null;
    };

    const sendUploadProcessedUploadDataToUser = async (
        albumId: number | null,
        medias: {
            fileName: string;
            name?: string;
            description?: string;
            taggedCreators?: { id: number; name: string }[];
            taggedReleaseForms?: { id: number; name: string }[];
            processedMedia: OnlyFansFileUploadResponse.Response;
        }[],
    ): Promise<{ success: boolean; addToAlbumSuccess: boolean }> => {
        if (userContext.jwtToken && settingsContext.apiKey && userContext.user.username) {
            const query: string = `${settingsContext.routes.onlyFans.base}${params.userId}/vaultMediaSendToUser`;

            return axios
                .post(query, {
                    albumId,
                    medias,
                })
                .then(async response => {
                    if (response.status === 200) {
                        return response.data;
                    }
                    return { success: false, addToAlbumSuccess: false };
                })
                .catch(error => {
                    console.error(error);
                    return { success: false, addToAlbumSuccess: false };
                });
        }
        return { success: false, addToAlbumSuccess: false };
    };

    /**
     * Formats bytes to human-readable format
     */
    const formatBytes = (bytes: number): string => {
        if (bytes === 0) return '0 B/s';
        const k = 1024;
        const sizes = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    };

    /**
     * Update aggregated progress from all parts
     */
    const updateAggregatedProgress = () => {
        const currentTime = Date.now();

        // Sum up all part progress
        let totalUploaded = uploadedBytesRef.current;
        partProgressRef.current.forEach(bytesUploaded => {
            totalUploaded += bytesUploaded;
        });

        // Calculate percentage
        const percentCompleted = Math.min(99, Math.round((totalUploaded * 100) / totalBytesRef.current));
        setProgress(percentCompleted);

        // Calculate speed with smoothing
        const timeDiff = (currentTime - lastProgressRef.current.time) / 1000;
        const bytesDiff = totalUploaded - lastProgressRef.current.loaded;

        if (timeDiff > 0 && bytesDiff > 0) {
            const instantSpeed = bytesDiff / timeDiff;

            // Exponential moving average for smooth speed
            const alpha = 0.2; // Lower = smoother
            smoothedSpeedRef.current =
                smoothedSpeedRef.current === 0 ? instantSpeed : alpha * instantSpeed + (1 - alpha) * smoothedSpeedRef.current;

            setUploadSpeed(smoothedSpeedRef.current);

            // Calculate ETA
            const remainingBytes = totalBytesRef.current - totalUploaded;
            const estimatedSecondsRemaining = remainingBytes / smoothedSpeedRef.current;

            if (isFinite(estimatedSecondsRemaining) && estimatedSecondsRemaining > 0) {
                setEta(Math.min(estimatedSecondsRemaining, 7200)); // Max 2 hours
            }

            lastProgressRef.current = { loaded: totalUploaded, time: currentTime };
        }
    };

    /**
     * Initialize progress tracking for multiple files
     */
    const initializeProgress = (files: File[], fileIndex: number = 0) => {
        const total = files.reduce((sum, file) => sum + file.size, 0);
        totalBytesRef.current = total;
        uploadedBytesRef.current = 0;
        setTotalFiles(files.length);
        setCurrentFileIndex(fileIndex);
        setUploading(true);
        setError(null);
        setProgress(0);
        setEta(null);
        setUploadSpeed(0);
        uploadStartTimeRef.current = Date.now();
        lastProgressRef.current = { loaded: 0, time: Date.now() };
        abortControllerRef.current = new AbortController();

        // Start smooth progress update interval
        if (progressUpdateIntervalRef.current) {
            clearInterval(progressUpdateIntervalRef.current);
        }

        progressUpdateIntervalRef.current = setInterval(() => {
            updateAggregatedProgress();
        }, 200); // Update every 200ms for smooth display
    };

    /**
     * Update current file being uploaded
     */
    const updateCurrentFile = (fileName: string, fileIndex: number) => {
        setCurrentFileName(fileName);
        setCurrentFileIndex(fileIndex);
    };

    /**
     * Formats seconds to human-readable time
     */
    const formatTime = (seconds: number): string => {
        if (!seconds || !isFinite(seconds)) return 'calculating...';

        if (seconds < 60) {
            return `${Math.round(seconds)}s`;
        } else if (seconds < 3600) {
            const minutes = Math.floor(seconds / 60);
            const secs = Math.round(seconds % 60);
            return `${minutes}m ${secs}s`;
        } else {
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            return `${hours}h ${minutes}m`;
        }
    };

    /**
     * Uploads a file to the given signed URL with overall progress tracking
     */
    const uploadFileToSignedUrl = async (
        signedUrl: string,
        file: File | Blob,
        partId: string = 'default', // Unique identifier for this part
    ): Promise<string | null> => {
        if (!file) {
            throw new Error('No file selected');
        }

        const mediaType = file.type || 'application/octet-stream';

        // Initialize progress for this part
        partProgressRef.current.set(partId, 0);

        try {
            const putResponse = await cleanAxios.default.put(signedUrl, file, {
                headers: {
                    'Content-Type': mediaType,
                },
                signal: abortControllerRef.current?.signal,
                onUploadProgress: progressEvent => {
                    // Update progress for this specific part
                    const loaded = progressEvent.loaded;
                    partProgressRef.current.set(partId, loaded);
                    // Aggregated progress update happens in the interval
                },
            });

            const eTag = putResponse.headers.etag || null;

            // Mark this part as complete
            partProgressRef.current.delete(partId);
            uploadedBytesRef.current += file.size;

            return eTag;
        } catch (err: any) {
            console.error(err);

            // Clean up progress tracking for this part
            partProgressRef.current.delete(partId);

            if (isCancel(err) || err.code === 'ERR_CANCELED') {
                setError('Upload cancelled');
            } else {
                setError(err?.message || 'Upload failed');
            }

            throw err;
        }
    };

    /**
     * Aborts the current upload
     */
    const abortUpload = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
    };

    /**
     * Resets the upload state
     */
    const resetUpload = () => {
        setUploading(false);
        setError(null);
        setProgress(0);
        setEta(null);
        setUploadSpeed(0);
        setCurrentFileName('');
        setTotalFiles(0);
        setCurrentFileIndex(0);
        uploadStartTimeRef.current = null;
        lastProgressRef.current = { loaded: 0, time: 0 };
        totalBytesRef.current = 0;
        uploadedBytesRef.current = 0;
        partProgressRef.current = new Map();
        smoothedSpeedRef.current = 0;

        if (progressUpdateIntervalRef.current) {
            clearInterval(progressUpdateIntervalRef.current);
            progressUpdateIntervalRef.current = null;
        }

        if (abortControllerRef.current) {
            abortControllerRef.current = null;
        }
    };

    return {
        createSignKeyForFile,
        createSignedUpload,
        checkVaultMediaHashExists,
        sendUploadDataToConverter,
        sendUploadProcessedUploadDataToUser,
        uploadFileToSignedUrl,
        finishSignedMultipartUpload,
        initializeProgress,
        updateCurrentFile,
        abortUpload,
        resetUpload,
        uploading,
        progress,
        eta: eta !== null ? formatTime(eta) : null,
        uploadSpeed: formatBytes(uploadSpeed),
        currentFileName,
        totalFiles,
        currentFileIndex,
        error,
    };
};

export default useSubscriberUpload;
